import { cast } from '../../../../../../utils/cast';
import { Id, ItemType, Relationship } from '../types';
import {
  itemRef,
  itemsRef,
  relationshipRef,
  relationshipsRef,
} from './db-refs';

async function getItems(
  userId: Id,
  listId: Id
): Promise<Record<string, ItemType>> {
  const items = {} as Record<string, ItemType>;
  const querySnapshot = await itemsRef({ uid: userId, listId }).get();
  querySnapshot.forEach((item) => {
    items[item.id] = { id: item.id, ...cast<ItemType>(item.data()) };
  });
  return items;
}

async function upsertItem(userId: Id, listId: Id, item: ItemType): Promise<Id> {
  if (!item.id) throw new Error('Item ID is required');
  await itemRef({ uid: userId, listId, id: item.id }).set(item, {
    merge: true,
  });
  return item.id;
}

function deleteItemAndRelationships(
  userId: Id,
  listId: Id,
  itemId: Id,
  callback: () => void
) {
  if (!itemId) return;
  itemRef({ uid: userId, listId, id: itemId }).delete();
  deleteItemRelationships(userId, listId, itemId);
  callback && typeof callback === 'function' && callback();
}

async function getRelationships(
  userId: Id,
  listId: Id
): Promise<Record<string, Relationship & { id: string }>> {
  const relationships = {} as Record<string, Relationship & { id: string }>;
  const querySnapshot = await relationshipsRef({ uid: userId, listId }).get();
  querySnapshot.forEach((relationship) => {
    relationships[relationship.id] = {
      id: relationship.id,
      ...cast<Relationship>(relationship.data()),
    };
  });
  return relationships;
}

async function upsertRelationship(
  userId: Id,
  listId: Id,
  relationship: Relationship & { id: Id }
): Promise<Id> {
  if (!relationship.options || !relationship.selection) {
    throw new Error('Relationship options and selection are required');
  }
  await relationshipRef({
    uid: userId,
    listId,
    id: relationship.id,
  }).set(relationship, { merge: true });
  return relationship.id;
}

function deleteRelationship(
  userId: Id,
  listId: Id,
  relationshipId: Id,
  callback?: () => void
) {
  if (!relationshipId) return;
  relationshipRef({
    uid: userId,
    listId,
    id: relationshipId,
  }).delete();
  callback && typeof callback === 'function' && callback();
}

const getItemRelationships = (
  userId: Id,
  listId: Id,
  itemId: Id,
  callback: (
    relationships: Record<string, Relationship & { id: string }>
  ) => void
) => {
  const relationships = {} as Record<string, Relationship & { id: string }>;
  relationshipsRef({ uid: userId, listId })
    .where('options', 'array-contains', itemId)
    .get()
    .then((querySnapshot) => {
      querySnapshot.forEach((relationship) => {
        relationships[relationship.id] = {
          id: relationship.id,
          ...cast<Relationship>(relationship.data()),
        };
      });
      callback && typeof callback === 'function' && callback(relationships);
    });
};
function deleteItemRelationships(
  userId: Id,
  listId: Id,
  itemId: Id,
  callback?: (relationships: unknown) => void
) {
  getItemRelationships(userId, listId, itemId, (relationships) => {
    Object.keys(relationships).forEach((relationshipId) => {
      deleteRelationship(userId, listId, relationshipId);
    });
    callback && typeof callback === 'function' && callback(relationships);
  });
}

export {
  getItems,
  upsertItem,
  deleteItemAndRelationships,
  getRelationships,
  upsertRelationship,
  deleteRelationship,
  deleteItemRelationships,
};
